"use client"

import { useState, useEffect, useRef } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { Upload, X, Loader2, ChevronDown, Check } from "lucide-react"
import axios from "axios"
import { useAuth } from "../contexts/AuthContext"
import { useNavigate } from "react-router-dom"

export default function DailyReportTab() {
  const { user, token } = useAuth()
  const navigate = useNavigate()
  const [selectedUser, setSelectedUser] = useState("")
  const [selectedUserName, setSelectedUserName] = useState("")
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [caving, setCaving] = useState("")
  const [uselessStones, setUselessStones] = useState("")
  const [emeraldsFound, setEmeraldsFound] = useState("")
  const [notes, setNotes] = useState("")
  const [images, setImages] = useState([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [users, setUsers] = useState([])
  const [apiError, setApiError] = useState(null)
  const [success, setSuccess] = useState(false)
  const dropdownRef = useRef(null)

  // Check auth and role
  useEffect(() => {
    if (!user || !token) {
      setApiError("Please log in to submit reports")
      navigate("/login")
      return
    }
    if (user.role !== "Admin" && user.role !== "Super Admin") {
      setApiError("You must be an admin to submit reports")
      navigate("/")
      return
    }
  }, [user, token, navigate])

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axios.get("https://backend.emerald.azadnoori.com/api/v1/users")
        setUsers(response.data.data.users || [])
      } catch (error) {
        let errorMessage = "Failed to load users."
        if (error.response?.status === 401) {
          errorMessage = "Session expired. Please log in again."
          navigate("/login")
        }
        setApiError(errorMessage)
      }
    }
    fetchUsers()
  }, [navigate])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const handleImageUpload = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const newImages = Array.from(e.target.files).map((file) => ({
        file,
        preview: URL.createObjectURL(file),
      }))
      setImages((prevImages) => [...prevImages, ...newImages])
      e.target.value = null
    }
  }

  const removeImage = (index) => {
    setImages((prevImages) => {
      const newImages = [...prevImages]
      URL.revokeObjectURL(newImages[index].preview)
      newImages.splice(index, 1)
      return newImages
    })
  }

  useEffect(() => {
    return () => {
      images.forEach((image) => URL.revokeObjectURL(image.preview))
    }
  }, [images])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    setApiError(null)
    setSuccess(false)

    if (!selectedUser || !caving || !emeraldsFound) {
      setApiError("Please fill in all required fields.")
      setIsSubmitting(false)
      return
    }

    const formData = new FormData()
    formData.append("user", selectedUser)
    formData.append("caving", Number(caving) || 0)
    formData.append("uselessStones", Number(uselessStones) || 0)
    formData.append("emeraldsFound", Number(emeraldsFound) || 0)
    formData.append("date", new Date().toISOString())
    formData.append("notes", notes || "")
    images.forEach((image) => {
      formData.append("images", image.file)
    })

    try {
      const response = await axios.post("https://backend.emerald.azadnoori.com/api/v1/reports", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        withCredentials: true,
      })

      setSelectedUser("")
      setSelectedUserName("")
      setCaving("")
      setUselessStones("")
      setEmeraldsFound("")
      setNotes("")
      setImages([])
      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)

      localStorage.setItem("reportAdded", Date.now().toString())
      setTimeout(() => localStorage.removeItem("reportAdded"), 1000)
    } catch (error) {
      let errorMessage = error.response?.data?.message || "Failed to submit report."
      if (error.response?.status === 401) {
        errorMessage = "Session expired. Please log in again."
        navigate("/login")
      }
      setApiError(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleUserSelect = (userId, userName) => {
    setSelectedUser(userId)
    setSelectedUserName(userName)
    setIsDropdownOpen(false)
  }

  const formVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div
        variants={formVariants}
        initial="hidden"
        animate="visible"
        className="flex flex-col lg:flex-row gap-8"
      >
        {/* Left Side - User Selection */}
        <motion.div variants={itemVariants} className="lg:w-1/3 relative z-10">
          <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-6">
            <h3 className="text-lg font-bold text-gray-900 mb-6">Select User</h3>

            <div className="relative z-20" ref={dropdownRef}>
              <button
                type="button"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="w-full bg-gray-50 border-2 border-gray-200 rounded-xl px-4 py-4 text-left flex items-center justify-between hover:border-blue-300 focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all duration-200"
              >
                <span className={selectedUserName ? "text-gray-900 font-medium" : "text-gray-500"}>
                  {selectedUserName || "Choose a user"}
                </span>
                <ChevronDown
                  className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
                    isDropdownOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              <AnimatePresence>
                {isDropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-xl z-[9999] overflow-hidden"
                    style={{ zIndex: 9999 }}
                  >
                    <div className="max-h-[360px] overflow-y-auto">
                      {users.map((user) => (
                        <button
                          key={user.id}
                          type="button"
                          onClick={() => handleUserSelect(user.id, `${user.firstname} ${user.lastname}`)}
                          className="w-full px-4 py-4 text-left hover:bg-blue-50 flex items-center justify-between group transition-colors duration-150 border-b border-gray-100 last:border-b-0"
                        >
                          <div className="flex flex-col">
                            <span className="text-gray-900 group-hover:text-blue-700 font-medium">
                              {user.firstname} {user.lastname}
                            </span>
                            <span className="text-xs text-gray-500">ID: {user.id}</span>
                          </div>
                          {selectedUser === user.id && <Check className="w-5 h-5 text-blue-600" />}
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {selectedUserName && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200"
              >
                <p className="text-sm text-blue-700">
                  <span className="font-medium">Selected:</span> {selectedUserName}
                </p>
              </motion.div>
            )}
          </div>
        </motion.div>

        {/* Right Side - Form */}
        <motion.div variants={itemVariants} className="lg:w-2/3">
          <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-lg p-6">
            {success && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-xl p-4 mb-6"
              >
                ✅ Report submitted successfully!
              </motion.div>
            )}
            {apiError && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 mb-6"
              >
                ❌ {apiError}
              </motion.div>
            )}

            <h3 className="text-lg font-bold text-gray-900 mb-6">Daily Report Details</h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
              <motion.div variants={itemVariants}>
                <label htmlFor="caving" className="block text-sm font-semibold text-gray-800 mb-3">
                  Amount of Caving
                </label>
                <input
                  id="caving"
                  type="number"
                  min="0"
                  value={caving}
                  onChange={(e) => setCaving(e.target.value)}
                  className="w-full bg-gray-50 border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all duration-200"
                  required
                />
              </motion.div>

              <motion.div variants={itemVariants}>
                <label htmlFor="uselessStones" className="block text-sm font-semibold text-gray-800 mb-3">
                  Useless Stones
                </label>
                <input
                  id="uselessStones"
                  type="number"
                  min="0"
                  value={uselessStones}
                  onChange={(e) => setUselessStones(e.target.value)}
                  className="w-full bg-gray-50 border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all duration-200"
                />
              </motion.div>

              <motion.div variants={itemVariants} className="sm:col-span-2 lg:col-span-1">
                <label htmlFor="emeraldsFound" className="block text-sm font-semibold text-gray-800 mb-3">
                  Emeralds Found
                </label>
                <input
                  id="emeraldsFound"
                  type="number"
                  min="0"
                  value={emeraldsFound}
                  onChange={(e) => setEmeraldsFound(e.target.value)}
                  className="w-full bg-gray-50 border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all duration-200"
                  required
                />
              </motion.div>
            </div>

            <motion.div variants={itemVariants} className="mb-6">
              <label htmlFor="notes" className="block text-sm font-semibold text-gray-800 mb-3">
                Notes
              </label>
              <textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full bg-gray-50 border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all duration-200 resize-none"
                rows="4"
                placeholder="Add any additional notes about the report..."
              />
            </motion.div>

            <motion.div variants={itemVariants} className="mb-6">
              <label className="block text-sm font-semibold text-gray-800 mb-3">Upload Images</label>
              <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-blue-400 transition-colors duration-200">
                <input
                  id="images"
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
                <label htmlFor="images" className="flex flex-col items-center justify-center cursor-pointer">
                  <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center mb-3">
                    <Upload className="h-6 w-6 text-blue-600" />
                  </div>
                  <span className="text-sm font-medium text-gray-700">Click to upload images</span>
                  <span className="text-xs text-gray-500 mt-1">PNG, JPG, GIF up to 10MB</span>
                </label>
              </div>
            </motion.div>

            {images.length > 0 && (
              <motion.div variants={itemVariants} className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
                {images.map((image, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={image.preview || "/placeholder.svg"}
                      alt={`Preview ${index + 1}`}
                      className="h-24 w-full object-cover rounded-lg bg-gray-100"
                      onError={(e) => {
                        e.target.src = "/placeholder.svg"
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </motion.div>
            )}

            <motion.div variants={itemVariants} className="flex justify-end">
              <button
                type="submit"
                disabled={isSubmitting}
                className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-8 py-3 rounded-xl font-semibold flex items-center justify-center min-w-[140px] hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-4 focus:ring-blue-200 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
              >
                {isSubmitting ? (
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{
                      repeat: Number.POSITIVE_INFINITY,
                      duration: 1,
                      ease: "linear",
                    }}
                  >
                    <Loader2 size={20} />
                  </motion.div>
                ) : (
                  "Submit Report"
                )}
              </button>
            </motion.div>
          </form>
        </motion.div>
      </motion.div>
    </div>
  )
}
